import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { RootState } from 'interfaces';
import ConfirmDialog from 'dialogs/ConfirmDialog';
import { ArticleDialog, ArticleHistoryDialog, CloneArticleDialog, MoveArticleDialog } from '../components';
import { Confluence } from '../interfaces';
import { useDeleteArticle, useExportArticlePdf, useShowOrHideArticle } from '../hooks';
import { TEMPLATE_TYPES } from '../helpers';

interface IProps {
  article: Confluence.IArticleBase | null;
  menu: Confluence.IMenu;
  onUpdate(article: Confluence.IArticleBase): void;
  onShowOrHide(article: Confluence.IArticleBase): void;
  onDelete(article: Confluence.IArticleBase): void;
  onMove(article: Confluence.IArticleBase, section: Confluence.ESections): void;
  onClone(cloneId: number, section: Confluence.ESections): void;
}

const ArticleActions = ({ article, menu, onUpdate, onDelete, onShowOrHide, onMove, onClone }: IProps) => {
  const operations = useSelector((state: RootState) => state.operations);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [updateArticleDialogOpened, setOpenedUpdateArticeDialog] = useState(false);
  const [hideArticleDialogOpened, setOpenedHideArticleDialog] = useState(false);
  const [deleteArticleDialogOpened, setOpenedDeleteArticleDialog] = useState(false);
  const [moveArticleDialogOpened, setOpenedMoveArticleDialog] = useState(false);
  const [cloneArticleDialogOpened, setCloneArticleDialogOpened] = useState(false);
  const [articleHistoryDialogOpened, setOpenedArticleHistoryDialog] = useState(false);

  const { handleShowOrHideArticle, loading: showOrHideArticleLoading } = useShowOrHideArticle(article, (article) => {
    onShowOrHide(article);
    setAnchorEl(null);
    setOpenedHideArticleDialog(false);
  });

  const { handleDeleteArticle, loading: deleteArticleLoading } = useDeleteArticle(article, (article) => {
    onDelete(article);
    setAnchorEl(null);
    setOpenedDeleteArticleDialog(false);
  });
  const { handleExportArticlePdf, loading: exportPdfArticleLoading } = useExportArticlePdf(article);

  const [, operation] = TEMPLATE_TYPES.get(article?.templateType || Confluence.ETemplateTypes.DEFAULT)!;

  return (
    <>
      <Tooltip title="Действия">
        <IconButton aria-label="Действия" onClick={(event) => setAnchorEl(event.currentTarget)}>
          <MoreVert />
        </IconButton>
      </Tooltip>

      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {operations.includes(operation) && (
          <MenuItem onClick={() => setOpenedUpdateArticeDialog(true)} key="Редактировать">
            Редактировать
          </MenuItem>
        )}

        {operations.includes(operation) && [
          article?.hide ? (
            <MenuItem disabled={showOrHideArticleLoading} onClick={handleShowOrHideArticle} key="Показать">
              Показать
            </MenuItem>
          ) : (
            <MenuItem onClick={() => setOpenedHideArticleDialog(true)} key="Скрыть">
              Скрыть
            </MenuItem>
          ),
        ]}

        {operations.includes(operation) && article?.hide && (
          <MenuItem onClick={() => setOpenedDeleteArticleDialog(true)} key="Удалить">
            Удалить
          </MenuItem>
        )}

        <MenuItem disabled={exportPdfArticleLoading} onClick={handleExportArticlePdf} key="Скачать PDF">
          Скачать PDF
        </MenuItem>

        {operations.includes(operation) && (
          <MenuItem onClick={() => setOpenedMoveArticleDialog(true)} key="Перенести">
            Перенести
          </MenuItem>
        )}

        {operations.includes(operation) && (
          <MenuItem onClick={() => setCloneArticleDialogOpened(true)} key="Клонировать">
            Клонировать
          </MenuItem>
        )}

        <MenuItem onClick={() => setOpenedArticleHistoryDialog(true)} key="История изменений">
          История изменений
        </MenuItem>
      </Menu>

      <ArticleDialog
        article={article}
        open={updateArticleDialogOpened}
        onClose={() => setOpenedUpdateArticeDialog(false)}
        onCreateOrUpdate={(article) => {
          onUpdate(article);
          setAnchorEl(null);
        }}
      />

      <ArticleHistoryDialog
        article={article}
        open={articleHistoryDialogOpened}
        onClose={() => setOpenedArticleHistoryDialog(false)}
      />

      <MoveArticleDialog
        article={article}
        menu={menu}
        open={moveArticleDialogOpened}
        onClose={() => setOpenedMoveArticleDialog(false)}
        onMove={(article, section) => {
          onMove(article, section);
          setAnchorEl(null);
        }}
      />

      <CloneArticleDialog
        article={article}
        menu={menu}
        open={cloneArticleDialogOpened}
        onClose={() => setCloneArticleDialogOpened(false)}
        onClone={(cloneId, section) => {
          onClone(cloneId, section);
          setAnchorEl(null);
        }}
      />

      <ConfirmDialog
        open={deleteArticleDialogOpened}
        disabled={deleteArticleLoading}
        title="Вы действительно хотите удалить статью?"
        onClose={() => setOpenedDeleteArticleDialog(false)}
        onConfirm={handleDeleteArticle}
      />

      <ConfirmDialog
        open={hideArticleDialogOpened}
        disabled={showOrHideArticleLoading}
        title="Вы действительно хотите скрыть статью?"
        onClose={() => setOpenedHideArticleDialog(false)}
        onConfirm={handleShowOrHideArticle}
      />
    </>
  );
};

export default ArticleActions;
